import "./insights_setup.scss";
import { useContext, useEffect, useState } from "react";
import AppLifecycleContext from "@/lib/contexts/AppLifecycleContext";
import { ConditionItem } from "types/types.d";
import { InsightsService } from "@/lib/services/InsightsService";
import { NameSetup } from "../Components/Setup/NameSetup";
import { EventsSetup } from "../Components/Setup/EventsSetup";
import { useNavigate } from "react-router-dom";
import { LoadingComponent } from "@/ui/components/Common/Loading/Loading";
import { toast } from "react-toastify";
import { TOAST_MESSAGES } from "@/lib/utils/Constants";
import { InsightContext } from "@/lib/contexts/InsightContext";
import useInsightHook from "@/hooks/useInsightHook";
import { RetentionSelectRatio } from "../Components/Setup/RetentionSelectRatio";
import { ThirdPartyTracking } from "@/lib/utils/ThirdPartyTracking";
import { Button } from "@mantine/core";
import { SidePanel } from "@/ui/components/Common/SidePanel/SidePanel";
import useConditionsValidityHook from "@/hooks/useConditionsValidityHook";
import useInsightsListHook from "@/hooks/useInsightsListHook";

interface IUsersRetentionSetupModal {
  isModalOpen: boolean;
  setIsModalOpen: (e: boolean) => void;
  insightId?: string;
}

const UserRetentionSetupModal = ({
  isModalOpen,
  setIsModalOpen,
  insightId
}: IUsersRetentionSetupModal) => {
  // States that are used.
  const [isLoading, setIsLoading] = useState("idle");
  const [templateId, setTemplateId] = useState("");
  const [name, setName] = useState("");
  const [signUpConditions, setSignUpConditions] = useState<Array<ConditionItem>>([]);
  const [signUpConditionType, setSignUpConditionType] = useState<"or" | "funnel" | undefined>("or");
  const [conditions, setConditions] = useState<Array<ConditionItem>>([]);
  const [conditionType, setConditionType] = useState<"or" | "funnel" | undefined>("or");
  const [retentionFrequency, setRetentionFrequency] = useState("daily");

  const [isStoreLoader, setIsStoreLoader] = useState("idle");
  const navigate = useNavigate();
  const { stepsValidation } = useConditionsValidityHook();
  // We are passing attributes and events to the child components.
  const { insightEvents, insightAttributes } = useInsightHook();
  const { activeWorkspace } = useContext(AppLifecycleContext);
  const { insightsList } = useInsightsListHook("retention");

  // NOTE: We are going to provide cohort filter in insights as discussed internally.

  // const [audienceConditionType, setAudienceConditionType] = useState<
  //   "or" | "and"
  // >("or");

  // const [audienceConditions, setAudienceConditions] = useState<
  //   Array<ConditionItem>
  // >([]);

  // all service calls
  const insightService = new InsightsService();
  const createInsight = async () => {
    setIsStoreLoader("loading");

    await insightService
      .create(activeWorkspace.id, {
        name: name,
        conditions: {
          retention_signed_up_conditions: signUpConditions,
          retention_signed_up_condition_type: "or",
          retention_conditions: conditions,
          retention_condition_type: "or",
          retention_frequency: retentionFrequency
        },
        type: "user_retention"
      })
      .then((res) => {
        if (res.data) {
          toast.success(TOAST_MESSAGES.INSIGHT_CREATED);
          const TPService = new ThirdPartyTracking();
          TPService.track("user_retention_insight_created");
          setIsModalOpen(false);
          // as the user is already on /env/${activeWorkspace.identifier}/insights/dashboard/retention
          navigate({ search: `insightId=${res.data.id}` });
        }
      })
      .catch((err) => {});
    setIsStoreLoader("loaded");
  };

  const updateInsight = async () => {
    setIsStoreLoader("loading");

    await insightService
      .update(activeWorkspace.id, templateId, {
        name: name,
        conditions: {
          retention_signed_up_conditions: signUpConditions,
          retention_signed_up_condition_type: "or",
          retention_conditions: conditions,
          retention_condition_type: "or",
          retention_frequency: retentionFrequency
        }
      })
      .then((res) => {
        toast.success(TOAST_MESSAGES.INSIGHT_UPDATED);
        setIsModalOpen(false);
      })
      .catch((err) => {});
    setIsStoreLoader("loaded");
  };

  const getInsight = async () => {
    setIsLoading("loading");
    await insightService
      .get(activeWorkspace.id, insightId)
      .then((res) => {
        if (res.data) {
          setTemplateId(res.data.id);
          setName(res.data.name);
          // document.title = `${res.data.name} | Insight | Usermaven`;
          setConditions(res.data.conditions.retention_conditions);
          setSignUpConditions(res.data.conditions.retention_signed_up_conditions);
          setSignUpConditionType(res.data.conditions.retention_signed_up_condition_type);
          setConditionType(res.data.conditions.retention_condition_type);
          setRetentionFrequency(res.data.conditions.retention_frequency);
        }
      })
      .catch((err) => {});
    setIsLoading("loaded");
  };

  useEffect(() => {
    if (insightId) {
      getInsight();
    } else {
      setIsLoading("loaded");
    }
  }, [insightId]);

  if (isLoading !== "loaded") {
    return (
      <>
        <LoadingComponent></LoadingComponent>
      </>
    );
  }

  const handleSubmission = async () => {
    if (name.length === 0) {
      toast.error("Please enter a name for your insight.");
      return;
    }

    // Check if the insight name already exists
    if (
      insightsList &&
      insightsList.find(
        (insight: any) =>
          insight.name.toLowerCase() === name.trim().toLowerCase() &&
          insight.id !== insightId &&
          insight.type === "user_retention"
      )
    ) {
      toast.error("An insight with the same name already exists. Please enter a different name.");
      return;
    }

    const userRetentionConditionsCheck = stepsValidation(conditions, 1, 10);
    if (!userRetentionConditionsCheck) {
      return;
    }

    const signUpConditionsCheck = stepsValidation(signUpConditions, 1, 10);
    if (!signUpConditionsCheck) {
      return;
    }

    if (insightId) {
      await updateInsight();
    } else {
      await createInsight();
    }
  };

  return (
    <SidePanel
      opened={isModalOpen}
      onCancel={() => setIsModalOpen(false)}
      loading={true}
      title={"User Retention"}>
      <NameSetup
        name={name}
        setName={setName}
        placeholder={"e.g User retention who have published content..."}
      />
      <InsightContext.Provider
        value={{
          insightEvents,
          insightAttributes
        }}>
        <EventsSetup
          conditions={signUpConditions}
          setConditions={setSignUpConditions}
          title={"Sign up event"}
          description={"Select an event users trigger when they sign up for your product."}
          minimumConditions={1}
          conditionType={signUpConditionType}
          stepNo={2}
        />
        <EventsSetup
          conditions={conditions}
          setConditions={setConditions}
          title={"Select events you want to measure retention for"}
          description={"Add some events that makes a user activity in your product."}
          conditionType={conditionType}
          stepNo={3}
        />
        <RetentionSelectRatio
          retentionFrequency={retentionFrequency}
          setRetentionFrequency={setRetentionFrequency}
        />
      </InsightContext.Provider>
      <div className="um-insights-setup__actions">
        <Button
          onClick={() => {
            setIsModalOpen(false);
          }}
          color="red">
          Cancel
        </Button>
        {templateId ? (
          <>
            <Button
              onClick={() => {
                handleSubmission();
              }}
              loading={isStoreLoader === "loading"}
              disabled={conditions.length === 0 || isStoreLoader === "loading"}>
              Update Insight
            </Button>
          </>
        ) : (
          <>
            <Button
              onClick={() => {
                handleSubmission();
              }}
              loading={isStoreLoader === "loading"}
              disabled={conditions.length === 0 || isStoreLoader === "loading"}>
              Save Insight
            </Button>
          </>
        )}
      </div>
    </SidePanel>
  );
};

export default UserRetentionSetupModal;
